import React, { useEffect, useState } from "react";
import { Doughnut, Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
} from "chart.js";
import {
    FiChevronRight,
    FiUsers,
    FiTool,
    FiStar,
    FiShield,
    FiMessageCircle,
    FiCheckCircle,
    FiXCircle,
    FiMinusCircle,
    FiSmile,
    FiMeh,
    FiFrown,
    FiTrendingUp,
    FiTrendingDown,
    FiZap,
    FiActivity
} from "react-icons/fi";
import { ImFilter } from "react-icons/im";
import { FaFilter } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUndo } from "@fortawesome/free-solid-svg-icons";

import Checkbox from "@mui/material/Checkbox";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
    FormControl,
    InputLabel,
    Select,
    OutlinedInput,
    MenuItem,
    ListItemText,
} from "@mui/material";
import axios from "axios";
import { baseURLFinanceVendor } from "config/apiConfig";
import { getStoredArray } from "utils/imageUtils";
import MainCard from "components/MainCard";
import BackButton from "components/BackButton";

ChartJS.register(
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement
);

const getTodayDate = () => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
};

const getPreviousMonthDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    date.setHours(0, 0, 0, 0);
    return date;
};

const formatDate = (date) => {
    if (!date) return null;
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const summaryCardsData = [
    {
        id: 1,
        title: "Total Feedbacks",
        value: 2486,
        change: "12%",
        changeType: "up",
        icon: FiMessageCircle,
        bg: "bg-violet-50",
        iconBg: "bg-violet-100",
        iconColor: "text-violet-600",
        valueColor: "text-violet-700",
    },
    {
        id: 2,
        title: "Positive Feedbacks",
        value: 1843,
        pct: 74.2,
        change: "8%",
        changeType: "up",
        icon: FiSmile,
        bg: "bg-emerald-50",
        iconBg: "bg-emerald-100",
        iconColor: "text-emerald-600",
        valueColor: "text-emerald-700",
    },
    {
        id: 3,
        title: "Neutral Feedbacks",
        value: 463,
        pct: 18.6,
        change: "3%",
        changeType: "down",
        icon: FiMeh,
        bg: "bg-sky-50",
        iconBg: "bg-sky-100",
        iconColor: "text-sky-600",
        valueColor: "text-sky-700",
    },
    {
        id: 4,
        title: "Negative Feedbacks",
        value: 180,
        pct: 7.2,
        change: "5%",
        changeType: "down",
        icon: FiFrown,
        bg: "bg-rose-50",
        iconBg: "bg-rose-100",
        iconColor: "text-rose-500",
        valueColor: "text-rose-600",
    },
];

const metricCardsData = [
    {
        id: 1,
        title: "Customer Loyalty",
        question: "Will you continue to use our service in the future?",
        icon: FiUsers,
        iconBg: "bg-violet-100",
        iconColor: "text-violet-600",
        total: 2486,
        yesPct: 78.4,
        yesCount: 1948,
        noPct: 12.6,
        noCount: 313,
        skipPct: 9.0,
        skipCount: 225,
    },
    {
        id: 2,
        title: "Repair Quality",
        question: "Were you satisfied with the quality of the repair?",
        icon: FiTool,
        iconBg: "bg-violet-100",
        iconColor: "text-violet-600",
        total: 2486,
        yesPct: 72.3,
        yesCount: 1797,
        noPct: 16.8,
        noCount: 418,
        skipPct: 10.9,
        skipCount: 271,
    },
    {
        id: 3,
        title: "Customer Connect",
        question: "Did our team stay in touch with you during the service?",
        icon: FiUsers,
        iconBg: "bg-violet-100",
        iconColor: "text-violet-600",
        total: 2486,
        yesPct: 71.5,
        yesCount: 1777,
        noPct: 18.2,
        noCount: 452,
        skipPct: 10.3,
        skipCount: 257,
    },
    {
        id: 4,
        title: "Excellent Approach",
        question: "Was the staff approach polite and professional?",
        icon: FiStar,
        iconBg: "bg-violet-100",
        iconColor: "text-violet-600",
        total: 2486,
        yesPct: 76.8,
        yesCount: 1909,
        noPct: 14.7,
        noCount: 365,
        skipPct: 8.5,
        skipCount: 212,
    },
    {
        id: 5,
        title: "Transparency",
        question: "Were the repair details and charges explained clearly?",
        icon: FiShield,
        iconBg: "bg-violet-100",
        iconColor: "text-violet-600",
        total: 2486,
        yesPct: 70.2,
        yesCount: 1745,
        noPct: 20.4,
        noCount: 507,
        skipPct: 9.4,
        skipCount: 234,
    },
];

const ratingData = {
    labels: ["1 – 5", "6 – 8", "9 – 10"],
    percentages: [18.2, 34.7, 47.1],
    counts: [452, 862, 1172],
    colors: ["#a78bfa", "#60a5fa", "#34d399"],
};

const overallSummaryData = [
    {
        id: 1,
        label: "Positive",
        pct: 74.2,
        change: "8%",
        changeType: "up",
        icon: FiSmile,
        bg: "bg-emerald-50",
        iconBg: "bg-emerald-100",
        iconColor: "text-emerald-600",
        valueColor: "text-emerald-700",
    },
    {
        id: 2,
        label: "Neutral",
        pct: 18.6,
        change: "3%",
        changeType: "down",
        icon: FiMeh,
        bg: "bg-sky-50",
        iconBg: "bg-sky-100",
        iconColor: "text-sky-600",
        valueColor: "text-sky-700",
    },
    {
        id: 3,
        label: "Negative",
        pct: 7.2,
        change: "5%",
        changeType: "down",
        icon: FiFrown,
        bg: "bg-rose-50",
        iconBg: "bg-rose-100",
        iconColor: "text-rose-500",
        valueColor: "text-rose-600",
    },
];

const keyInsightsData = [
    "Customer loyalty and transparency have the highest positive response rates.",
    "Rating shows 47.1% of customers gave a score of 9–10.",
    "Negative feedback is highest for Repair Quality (16.8%).",
];

const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "60%",
    animation: { duration: 450 },
    plugins: {
        legend: { display: false },
        tooltip: {
            enabled: true,
            backgroundColor: "#1f2937",
            titleFont: { size: 15 },
            bodyFont: { size: 15 },
            padding: 8,
            cornerRadius: 6,
        },
    },
};

function makeDoughnutData(yes, no, skip) {
    return {
        labels: ["Yes", "No", "Skip"],
        datasets: [
            {
                data: [yes, no, skip],
                backgroundColor: ["#22c55e", "#f87171", "#fbbf24"],
                borderWidth: 2,
                borderColor: "#ffffff",
                spacing: 0,
                hoverOffset: 3,
            },
        ],
    };
}

const ratingChartData = {
    labels: ratingData.labels,
    datasets: [
        {
            data: ratingData.counts,
            backgroundColor: ratingData.colors,
            borderRadius: 6,
            barThickness: 54,
        },
    ],
};

const ratingOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { display: false },
        tooltip: {
            backgroundColor: "#1f2937",
            titleFont: { size: 15 },
            bodyFont: { size: 15 },
            padding: 8,
            cornerRadius: 6,
        },
    },
    scales: {
        x: {
            grid: { display: false },
            ticks: { display: false },
            border: { display: true, color: "#e8edf7", width: 1 },
        },
        y: {
            display: false,
            grid: { display: false },
        },
    },
};

function MetricCard({
    title,
    question,
    icon: Icon,
    iconBg,
    iconColor,
    total,
    yesPct,
    yesCount,
    noPct,
    noCount,
    skipPct,
    skipCount,
}) {
    const data = makeDoughnutData(yesCount, noCount, skipCount);

    return (
        <div className="flex h-full min-h-[186px] flex-col overflow-hidden rounded-[6px] border border-[#dce7ff] bg-white shadow-[0_2px_7px_rgba(55,85,150,0.06)]">
            <div className="flex min-h-[52px] shrink-0 items-center justify-between border-b border-[#e8eaff] bg-gradient-to-r from-[#f1efff] to-[#f7f8ff] px-[12px] py-[6px]">
                <div className="flex items-center gap-3">
                    <Icon className={`h-[19px] w-[19px] shrink-0 ${iconColor}`} />
                    <div className="min-w-0">
                        <h3 className="text-[15px] font-bold leading-5 text-[#5526d8]">{title}</h3>
                        <p className="text-[11px] leading-4 text-[#7180a6]">{question}</p>
                    </div>
                </div>
                <FiChevronRight className="h-4 w-4 text-[#6575b7]" />
            </div>
            <div className="flex min-h-[146px] items-center gap-[12px] px-[12px] py-[10px] sm:gap-[14px]">
                <div className="relative h-[108px] w-[108px] shrink-0 min-[480px]:h-[108px] min-[480px]:w-[108px] max-[380px]:h-[92px] max-[380px]:w-[92px]">
                    <Doughnut data={data} options={doughnutOptions} />
                    {/* <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-[17px] font-bold leading-5 text-[#172b66]">
                            {total.toLocaleString()}
                        </span>
                    </div> */}
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-[6px]">
                    {[
                        { label: "Yes", pct: yesPct, count: yesCount, color: "bg-[#19a875]", icon: <FiCheckCircle className="h-3.5 w-3.5 text-white" /> },
                        { label: "No", pct: noPct, count: noCount, color: "bg-[#ff4c73]", icon: <FiXCircle className="h-3.5 w-3.5 text-white" /> },
                        { label: "Skip", pct: skipPct, count: skipCount, color: "bg-[#ffb300]", icon: <FiMinusCircle className="h-3.5 w-3.5 text-white" /> },
                    ].map((item) => (
                        <div key={item.label} className="grid grid-cols-[17px_minmax(20px,1fr)_auto_44px] items-center gap-[7px] rounded-[5px] border border-[#edf2fa] bg-[#f7faff] px-[7px] py-[7px] sm:gap-[8px]">
                            <span className={`flex h-[17px] w-[17px] items-center justify-center rounded-full ${item.color}`}>{item.icon}</span>
                            <span className="text-[13px] text-[#66759c]">{item.label}</span>
                            <span className="text-[13px] font-bold text-[#293d79]">{item.pct}%</span>
                            <span className="w-[44px] text-right text-[13px] text-[#8a98b8]">{item.count.toLocaleString()}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function RatingCard() {
    return (
        <div className="flex h-full min-h-[186px] flex-col overflow-hidden rounded-[6px] border border-[#dce7ff] bg-white shadow-[0_2px_7px_rgba(55,85,150,0.06)]">
            <div className="flex min-h-[52px] shrink-0 items-center justify-between border-b border-[#e8eaff] bg-gradient-to-r from-[#f1efff] to-[#f7f8ff] px-[12px] py-[6px]">
                <div className="flex items-center gap-3">
                    <FiStar className="h-[19px] w-[19px] shrink-0 text-[#5526d8]" />
                    <div className="min-w-0">
                        <h3 className="text-[15px] font-bold leading-5 text-[#5526d8]">Rating</h3>
                        <p className="text-[11px] leading-4 text-[#7180a6]">How would you rate your overall experience (1–10)?</p>
                    </div>
                </div>
                <FiChevronRight className="h-4 w-4 text-[#6575b7]" />
            </div>
            <div className="flex flex-1 flex-col px-[12px] pt-[10px] pb-[7px]">
                <div className="mb-1 flex justify-between px-1">
                    {ratingData.labels.map((label, index) => (
                        <div key={label} className="flex-1 text-center">
                            <div className="text-[13px] font-semibold text-[#51659b]">{label}</div>
                            <div className="text-[15px] font-bold text-[#34477e]">{ratingData.percentages[index]}%</div>
                        </div>
                    ))}
                </div>
                <div className="h-[76px] w-full min-w-0">
                    <Bar data={ratingChartData} options={ratingOptions} />
                </div>
                <div className="mt-1 flex justify-between px-1">
                    {ratingData.counts.map((count, index) => (
                        <span key={index} className="flex-1 text-center text-[13px] text-[#687ba7]">
                            {count.toLocaleString()}
                        </span>
                    ))}
                </div>
                <div className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-t border-[#eef2fa] pt-2">
                    {ratingData.labels.map((label, index) => (
                        <div key={label} className="flex items-center gap-1">
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: ratingData.colors[index] }} />
                            <span className="text-[12px] text-[#7180a6]">
                                {label} ({ratingData.percentages[index]}%)
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function SummaryCard({
    title,
    value,
    pct,
    change,
    changeType,
    icon: Icon,
    bg,
    iconBg,
    iconColor,
    valueColor,
}) {
    return (
        <div className={`flex min-h-[100px] items-center gap-[12px] rounded-[7px] border border-[#e2e7ff] px-[12px] py-[10px] shadow-[0_2px_7px_rgba(55,85,150,0.04)] ${bg}`}>
            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full shadow-sm ${iconBg}`}>
                <Icon className={`h-[19px] w-[19px] ${iconColor}`} />
            </div>
            <div className="min-w-0 flex-1">
                <div className="mb-1 text-[12px] font-semibold text-[#53689c]">{title}</div>
                <div className={`text-[22px] font-bold leading-6 tracking-tight ${valueColor}`}>{value.toLocaleString()}</div>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                    {pct !== undefined && <span className="text-[13px] font-semibold text-[#243d7d]">{pct}%</span>}
                    <span className={`inline-flex items-center gap-0.5 text-[12px] font-semibold ${changeType === "up" ? "text-[#12a66c]" : "text-[#ff4545]"}`}>
                        {changeType === "up" ? <FiTrendingUp className="h-3 w-3" /> : <FiTrendingDown className="h-3 w-3" />}
                        {change}
                        {pct === undefined && <span className="ml-1 text-[#607199]">vs. previous period</span>}
                    </span>
                </div>
            </div>
        </div>
    );
}

const FeedbackCallingDashboard = () => {
    const localStorageDealer = getStoredArray("dealer");
    const localStorageVendor = getStoredArray("vendor");
    const today = new Date();

    const [startDate, setStartDate] = useState(getPreviousMonthDate());
    const [endDate, setEndDate] = useState(getTodayDate());
    const [selectedClients, setSelectedClients] = useState([]);
    const [selectedVendors, setSelectedVendors] = useState([]);

    const [clients, setClients] = useState([]);
    const [vendors, setVendors] = useState([]);

    const filteredClients =
        localStorageDealer.length > 0 ? localStorageDealer : clients;
    const filteredVendors =
        localStorageVendor.length > 0 ? localStorageVendor : vendors;

    const MenuProps = {
        PaperProps: {
            style: { maxHeight: 250, width: 260, borderRadius: 12 },
        },
    };

    const handleMultiSelect = (setter, allOptions) => (event) => {
        const { value } = event.target;
        const selectedValues = typeof value === "string" ? value.split(",") : value;

        if (selectedValues.includes("select-all")) {
            const isAllSelected = selectedValues.length - 1 === allOptions.length;
            if (isAllSelected) {
                setter([]);
            } else {
                setter([...allOptions]);
            }
        } else {
            setter(selectedValues);
        }
    };

    const handleDateChange = (type, value) => {
        if (type === "start") setStartDate(value);
        else setEndDate(value);
    };

    const applyFilters = () => {
        const payload = {
            startDate: formatDate(startDate),
            endDate: formatDate(endDate),
            clientName: selectedClients.length > 0 ? selectedClients : filteredClients,
            vendorName: selectedVendors.length > 0 ? selectedVendors : filteredVendors,
        };

        console.log("=== Apply Filters Payload ===");
        console.log(payload);
    };

    const resetFilters = () => {
        setSelectedClients([]);
        setSelectedVendors([]);
        setStartDate(getPreviousMonthDate());
        setEndDate(getTodayDate());

        console.log("=== Filters Reset ===");
    };

    useEffect(() => {
        const fetchFilterData = async () => {
            try {
                const token = localStorage.getItem("token");
                const headers = { Authorization: `Bearer ${token}` };

                const [vendorsRes, clientsRes] = await Promise.all([
                    axios.get(`${baseURLFinanceVendor}getAllVendorList`, { headers }),
                    axios.get(`${baseURLFinanceVendor}getAllClientFromBERSlab`, { headers }),
                ]);

                const vendorData = vendorsRes.data?.data || [];
                const uniqueVendors = [...new Set(vendorData.map((v) => v))]
                    .filter(Boolean)
                    .sort((a, b) => a.localeCompare(b));
                setVendors(uniqueVendors);

                setClients(clientsRes.data?.dealers || []);
            } catch (error) {
                console.error("Error fetching filter data:", error);
                setVendors([]);
                setClients([]);
            }
        };

        fetchFilterData();
    }, []);

    const firstRowMetrics = metricCardsData.slice(0, 3);
    const secondRowMetrics = metricCardsData.slice(3);

    const highestRatingIndex = ratingData.percentages.indexOf(
        Math.max(...ratingData.percentages)
    );

    const lowestRatingIndex = ratingData.percentages.indexOf(
        Math.min(...ratingData.percentages)
    );

    const promotersPct = ratingData.percentages[highestRatingIndex];
    const detractorsPct = ratingData.percentages[lowestRatingIndex];

    const nps = (promotersPct - detractorsPct).toFixed(2);

    return (
        <MainCard>
            <BackButton />
            <div className="mx-auto w-full space-y-[15px]">

                <div className="overflow-hidden rounded-[6px] border border-[#dce7ff] bg-white shadow-[0_2px_7px_rgba(55,85,150,0.06)]">
                    <div className="flex h-[42px] items-center gap-2 bg-gradient-to-r from-[#5526d8] via-[#6b3de8] to-[#7c4dff] px-[12px]">
                        <ImFilter className="text-white text-[17px]" />
                        <h3 className="text-[16px] font-bold text-white">Filter Selection</h3>
                    </div>

                    <div className="flex flex-wrap items-end justify-between gap-3 bg-[#f7faff] px-[12px] py-[12px]">
                        <div className="flex items-center gap-2">
                            <DatePicker
                                selected={startDate}
                                onChange={(d) => handleDateChange("start", d)}
                                selectsStart
                                startDate={startDate}
                                endDate={endDate}
                                maxDate={today}
                                placeholderText="Start Date"
                                isClearable={true}
                                className="w-[160px] rounded-[5px] border border-[#d0d8f0] px-3 py-[8px] text-[15px] outline-none transition-all focus:border-[#5526d8] focus:ring-1 focus:ring-[#5526d8]"
                                dateFormat="dd/MM/yyyy"
                            />
                            <span className="text-[15px] font-semibold text-[#6575b7]">to</span>
                            <DatePicker
                                selected={endDate}
                                onChange={(d) => handleDateChange("end", d)}
                                selectsEnd
                                startDate={startDate}
                                endDate={endDate}
                                maxDate={today}
                                placeholderText="End Date"
                                isClearable={true}
                                className="w-[160px] rounded-[5px] border border-[#d0d8f0] px-3 py-[8px] text-[15px] outline-none transition-all focus:border-[#5526d8] focus:ring-1 focus:ring-[#5526d8]"
                                dateFormat="dd/MM/yyyy"
                            />
                        </div>

                        <FormControl sx={{ width: 220, "& .MuiInputBase-root": { height: 42, fontSize: 15 } }}>
                            <InputLabel id="client-label" sx={{ fontSize: 15 }}>Select Client</InputLabel>
                            <Select
                                labelId="client-label"
                                multiple
                                value={selectedClients}
                                onChange={handleMultiSelect(setSelectedClients, filteredClients)}
                                input={<OutlinedInput label="Select Client" />}
                                renderValue={(selected) =>
                                    selected.length === filteredClients?.length
                                        ? "All Clients"
                                        : selected.join(", ")
                                }
                                MenuProps={MenuProps}
                                sx={{ borderRadius: "5px", fontSize: 15 }}
                            >
                                <MenuItem value="select-all">
                                    <Checkbox checked={selectedClients.length === filteredClients?.length} size="small" />
                                    <ListItemText primary="Select All" primaryTypographyProps={{ fontSize: 15 }} />
                                </MenuItem>
                                {filteredClients
                                    ?.slice()
                                    ?.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
                                    ?.map((client) => (
                                        <MenuItem key={client} value={client}>
                                            <Checkbox checked={selectedClients.includes(client)} size="small" />
                                            <ListItemText primary={client} primaryTypographyProps={{ fontSize: 15 }} />
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>

                        <FormControl sx={{ width: 220, "& .MuiInputBase-root": { height: 42, fontSize: 15 } }}>
                            <InputLabel id="vendor-label" sx={{ fontSize: 15 }}>Select Vendor</InputLabel>
                            <Select
                                labelId="vendor-label"
                                multiple
                                value={selectedVendors}
                                onChange={handleMultiSelect(setSelectedVendors, filteredVendors)}
                                input={<OutlinedInput label="Select Vendor" />}
                                renderValue={(selected) =>
                                    selected.length === filteredVendors?.length
                                        ? "All Vendors"
                                        : selected.join(", ")
                                }
                                MenuProps={MenuProps}
                                sx={{ borderRadius: "5px", fontSize: 15 }}
                            >
                                <MenuItem value="select-all">
                                    <Checkbox checked={selectedVendors.length === filteredVendors?.length} size="small" />
                                    <ListItemText primary="Select All" primaryTypographyProps={{ fontSize: 15 }} />
                                </MenuItem>
                                {filteredVendors
                                    ?.slice()
                                    ?.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
                                    ?.map((vendor) => (
                                        <MenuItem key={vendor} value={vendor}>
                                            <Checkbox checked={selectedVendors.includes(vendor)} size="small" />
                                            <ListItemText primary={vendor} primaryTypographyProps={{ fontSize: 15 }} />
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>

                        <div className="flex gap-2">
                            <button
                                onClick={applyFilters}
                                className="flex items-center gap-1.5 rounded-[5px] bg-gradient-to-r from-[#5526d8] to-[#7c4dff] px-4 py-[8px] text-[15px] font-semibold text-white shadow-sm transition-all hover:opacity-90"
                            >
                                <FaFilter className="text-[14px]" /> Apply Filters
                            </button>
                            <button
                                onClick={resetFilters}
                                className="flex items-center gap-1.5 rounded-[5px] bg-gradient-to-r from-gray-500 to-gray-600 px-4 py-[8px] text-[15px] font-semibold text-white shadow-sm transition-all hover:opacity-90"
                            >
                                <FontAwesomeIcon icon={faUndo} className="text-[14px]" /> Reset
                            </button>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 min-[480px]:grid-cols-2 min-[900px]:grid-cols-4 gap-[10px]">
                    {summaryCardsData.map((card) => (
                        <SummaryCard key={card.id} {...card} />
                    ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 min-[900px]:grid-cols-3 gap-[12px]">
                    {firstRowMetrics.map((card) => (
                        <MetricCard key={card.id} {...card} />
                    ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 min-[900px]:grid-cols-3 gap-[12px]">
                    {secondRowMetrics.map((card) => (
                        <MetricCard key={card.id} {...card} />
                    ))}
                    <RatingCard />
                </div>

                <div className="grid grid-cols-1 gap-[12px] md:grid-cols-2 min-[1200px]:grid-cols-3">
                    {/* Overall Feedback Summary */}
                    <div className="min-w-0 overflow-hidden rounded-lg border border-[#dce7ff] bg-white shadow-[0_2px_7px_rgba(55,85,150,0.06)]">
                        <div className="flex h-[38px] items-center gap-[8px] border-b border-[#e8eaff] bg-gradient-to-r from-[#f1efff] to-[#f7f8ff] px-[10px]">
                            <div className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full bg-violet-100">
                                <FiMessageCircle className="h-[14px] w-[14px] text-violet-600" />
                            </div>

                            <h3 className="text-[15px] font-bold text-[#5526d8]">
                                Overall Feedback Summary
                            </h3>
                        </div>

                        <div className="grid min-h-[54px] grid-cols-1 gap-2 px-[12px] py-[8px] sm:grid-cols-3 sm:gap-0">
                            {overallSummaryData.map((item) => {
                                const Icon = item.icon;

                                return (
                                    <div
                                        key={item.id}
                                        className="flex min-w-0 items-center gap-[10px] border-b border-[#e4eaff] px-[8px] py-[4px] last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0"
                                    >
                                        <div
                                            className={`flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full ${item.iconBg}`}
                                        >
                                            <Icon
                                                className={`h-5 w-5 ${item.iconColor}`}
                                            />
                                        </div>

                                        <div className="min-w-0">
                                            <div className="text-[12px] font-medium text-[#65769e]">
                                                {item.label}
                                            </div>

                                            <div className="flex flex-wrap items-center gap-2">
                                                <span
                                                    className={`text-[17px] font-bold ${item.valueColor}`}
                                                >
                                                    {item.pct}%
                                                </span>

                                                <span
                                                    className={`inline-flex items-center gap-0.5 text-[15px] font-medium ${item.changeType === "up"
                                                        ? "text-green-600"
                                                        : "text-red-500"
                                                        }`}
                                                >
                                                    {item.changeType === "up" ? (
                                                        <FiTrendingUp className="h-3.5 w-3.5 shrink-0" />
                                                    ) : (
                                                        <FiTrendingDown className="h-3.5 w-3.5 shrink-0" />
                                                    )}

                                                    {item.change}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>


                    <div className="min-w-0 overflow-hidden rounded-lg border border-[#dce7ff] bg-white shadow-[0_2px_7px_rgba(55,85,150,0.06)]">
                        <div className="flex h-[38px] items-center gap-[8px] border-b border-[#e8eaff] bg-gradient-to-r from-[#f1efff] to-[#f7f8ff] px-[10px]">
                            <div className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full bg-violet-100">
                                <FiMessageCircle className="h-[14px] w-[14px] text-violet-600" />
                            </div>

                            <h3 className="text-[15px] font-bold text-[#5526d8]">
                                NPS
                            </h3>
                        </div>

                        <div className="grid min-h-[54px] grid-cols-1 gap-2 px-[12px] py-[8px] sm:grid-cols-3 sm:gap-0">
                            {/* Net Promoter Score */}
                            <div className="flex min-w-0 items-center gap-[10px] border-b border-[#e4eaff] px-[8px] py-[4px] sm:border-b-0 sm:border-r">
                                <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-violet-100">
                                    <FiActivity className="h-5 w-5 text-violet-600" />
                                </div>

                                <div className="min-w-0">
                                    <div className="text-[12px] font-medium text-[#65769e]">
                                        Net Promoter Score
                                    </div>

                                    <div className="text-[17px] font-bold text-[#5526d8]">
                                        {nps}%
                                    </div>
                                </div>
                            </div>

                            {/* Promoters: Highest rating percentage */}
                            <div className="flex min-w-0 items-center gap-[10px] border-b border-[#e4eaff] px-[8px] py-[4px] sm:border-b-0 sm:border-r">
                                <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-green-100">
                                    <FiSmile className="h-5 w-5 text-green-600" />
                                </div>

                                <div className="min-w-0">
                                    <div className="text-[12px] font-medium text-[#65769e]">
                                        Promoters ({ratingData.labels[highestRatingIndex]})
                                    </div>

                                    <div className="text-[17px] font-bold text-green-600">
                                        {promotersPct}%
                                    </div>
                                </div>
                            </div>

                            {/* Detractors: Lowest rating percentage */}
                            <div className="flex min-w-0 items-center gap-[10px] px-[8px] py-[4px]">
                                <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-red-100">
                                    <FiFrown className="h-5 w-5 text-red-500" />
                                </div>

                                <div className="min-w-0">
                                    <div className="text-[12px] font-medium text-[#65769e]">
                                        Detractors ({ratingData.labels[lowestRatingIndex]})
                                    </div>

                                    <div className="text-[17px] font-bold text-red-500">
                                        {detractorsPct}%
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>


                    {/* Key Insights */}
                    <div className="min-w-0 overflow-hidden rounded-lg border border-[#dce7ff] bg-white shadow-[0_2px_7px_rgba(55,85,150,0.06)]">
                        <div className="flex h-[38px] items-center gap-[8px] border-b border-[#e8eaff] bg-gradient-to-r from-[#f1efff] to-[#f7f8ff] px-[10px]">
                            <div className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full bg-violet-100">
                                <FiZap className="h-[14px] w-[14px] text-violet-600" />
                            </div>

                            <h3 className="text-[15px] font-bold text-[#5526d8]">
                                Key Insights
                            </h3>
                        </div>

                        <ul className="space-y-[5px] px-[12px] py-[8px]">
                            {keyInsightsData.map((insight, index) => (
                                <li
                                    key={index}
                                    className="flex gap-[7px] text-[12px] leading-[1.45] text-[#65769e]"
                                >
                                    <span className="mt-[5px] h-[4px] w-[4px] shrink-0 rounded-full bg-[#5526d8]" />
                                    <span className="min-w-0 break-words">{insight}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </MainCard>
    );
};

export default FeedbackCallingDashboard;